/**
 * 图片裁剪 / 超分提质 / 智能抠图 / 主图创建 / 万能改图等批处理：
 * 与服务端 Redis 队列对齐，跨模块共享并发槽。
 *
 * 权威限流在后端（单用户 + 全站 + 模块）；本常量仅作展示/兜底对齐，
 * 勿再单独做更严的前端闸门。
 */
import { processingQueueAPI } from './api';

/**
 * 与服务端 ipe.redis.max-concurrent-processing-tasks（单用户）默认一致。
 * 可通过 REACT_APP_IPE_PROCESSING_MAX_CONCURRENT 覆盖。
 */
const parsed = Number(process.env.REACT_APP_IPE_PROCESSING_MAX_CONCURRENT);
export const IPE_PROCESSING_MAX_CONCURRENT =
  Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 4;
export const FUNDING_PROCESSING_MAX_CONCURRENT = IPE_PROCESSING_MAX_CONCURRENT;

const SLOT_POLL_MS = 2000;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * 等待并占用一个处理槽；isCancelled 返回 true 时放弃排队。
 * @returns {Promise<boolean>} 是否成功占用（取消则为 false）
 */
export async function waitForIpeProcessingSlot(taskId, module, { isCancelled } = {}) {
  const id = String(taskId);
  let waitedRounds = 0;
  for (;;) {
    if (typeof isCancelled === 'function' && isCancelled()) {
      return false;
    }
    try {
      const res = await processingQueueAPI.acquireSlot({ taskId: id, module });
      if (res?.acquired !== false) {
        if (waitedRounds > 0 || Number(res?.prunedStaleSlots) > 0) {
          console.info('[处理队列] 已占用槽', {
            taskId: id,
            module,
            waitedRounds,
            prunedStaleSlots: res?.prunedStaleSlots,
            runningCount: res?.runningCount,
          });
        }
        return true;
      }
      waitedRounds += 1;
      if (waitedRounds === 1 || waitedRounds % 15 === 0) {
        console.warn('[处理队列] 排队等待槽位', {
          taskId: id,
          module,
          runningCount: res?.runningCount,
          maxConcurrent: res?.maxConcurrentPerUser ?? res?.maxConcurrent,
          prunedStaleSlots: res?.prunedStaleSlots,
        });
      }
    } catch (e) {
      console.warn('[处理队列] acquire-slot 失败，本地直跑:', e);
      return true;
    }
    await sleep(SLOT_POLL_MS);
  }
}

export const waitForFundingProcessingSlot = waitForIpeProcessingSlot;

export async function releaseIpeProcessingSlot(taskId) {
  const id = String(taskId);
  try {
    await processingQueueAPI.releaseSlot(id);
  } catch {
    /* ignore */
  }
}

export const releaseFundingProcessingSlot = releaseIpeProcessingSlot;

/**
 * 在 Redis 并发槽保护下执行批处理；槽满时自动排队。
 */
export async function withIpeProcessingSlot(taskId, module, fn, { isCancelled } = {}) {
  const acquired = await waitForIpeProcessingSlot(taskId, module, { isCancelled });
  if (!acquired) {
    return undefined;
  }
  try {
    return await fn();
  } finally {
    await releaseIpeProcessingSlot(taskId);
  }
}

export const withFundingProcessingSlot = withIpeProcessingSlot;
